import { Tab } from "@headlessui/react";
import chroma from "chroma-js";
import invert from "invert-color";
import { rem } from "polished";
import * as React from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import styled, { css } from "styled-components";

import {
  ColorLabels,
  getColorByLabel,
} from "@/components/editor/utils/use-color";
import noScrollBar from "@/styles/no-scrollbar";

const TabListWrapper = styled.div`
  margin: 0 ${rem(-24)} ${rem(24)};
  max-width: 100vw;
  padding: ${rem(5)} 0;
  ${noScrollBar};
`;

const StyledTabList = styled(Tab.List)`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(${rem(100)}, 1fr);
  gap: ${rem(24)};
  background: none;
  margin: auto;
  padding: 0 ${rem(12)};
  width: max-content;
`;

function TabList(props) {
  return (
    <TabListWrapper>
      <StyledTabList {...props} />
    </TabListWrapper>
  );
}

const StyledTab = styled(Tab)`
  ${({ theme }) => {
    const themeColor = theme.colors[ColorLabels.BACKGROUND];
    const selectedColor = getColorByLabel(ColorLabels.PRIMARY_FONT);
    const unselectedColor =
      chroma.valid(themeColor) && invert(themeColor, true) === "#ffffff"
        ? "#a9a9a9"
        : "#6a6a6a";

    return css`
      position: relative;
      padding: 0 ${rem(12)};
      border: none;
      color: ${unselectedColor};
      font-weight: 600;
      font-family: ${({ theme }) =>
        theme.fontImports[0]?.font?.name ?? theme.fonts[0].value};
      font-size: ${rem(14)};
      line-height: ${rem(33)};
      text-transform: uppercase;

      &[data-headlessui-state~="selected"] {
        color: ${selectedColor};

        &::after {
          opacity: 1;
        }
      }

      &::after {
        content: "";
        height: ${rem(2)};
        width: 50%;
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        opacity: 0;
        background: ${selectedColor};
      }
    `;
  }}
`;

const TabPanels = styled(Tab.Panels)`
  position: relative;
  z-index: var(--z-index-lowest);
`;

function ScrollableTab(props) {
  const ownRef = React.useRef<HTMLButtonElement>(null);
  const isFirstRender = React.useRef(true);

  return (
    <StyledTab
      ref={ownRef}
      {...props}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        const node = ownRef.current;
        if (node && !isFirstRender.current) {
          scrollIntoView(node, {
            behavior: "smooth",
            inline: "center",
            block: "nearest",
          });
        }
        isFirstRender.current = false;
        props.onClick?.(e);
      }}
    />
  );
}

const Tabs = Tab.Group;
const TabPanel = Tab.Panel;

export { TabList, Tabs, ScrollableTab as Tab, TabPanels, TabPanel };
